import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchVouchersReportAction,
  setLoading,
} from "../slices/vouchersReportSlice";
import { FetchVouchersReportParams } from "../api/fetchVouchersReport/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

export interface VouchersReportFilters {
  searchTerm?: string;
  statuses?: string[];
}

function buildFilterSortParams(
  filters: VouchersReportFilters,
  sorting: SortingState,
): Omit<FetchVouchersReportParams, "page" | "pageSize"> {
  const params: Omit<FetchVouchersReportParams, "page" | "pageSize"> = {};
  if (filters.searchTerm) params.searchTerm = filters.searchTerm;
  if (filters.statuses?.length) params.statuses = filters.statuses;
  if (sorting.length > 0) {
    params.sortBy = sorting[0].id;
    params.sortOrder = sorting[0].desc ? "DESC" : "ASC";
  }
  return params;
}

export function useVouchersReport() {
  const dispatch = useAppDispatch();

  const { items, totalCount, loading, error } = useSelector(
    (state: RootState) => state.vouchersReport,
  );

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<VouchersReportFilters>({});

  useEffect(() => {
    dispatch(
      fetchVouchersReportAction({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...buildFilterSortParams(filters, sorting),
      }),
    );
  }, [dispatch, pagination, sorting, filters]);

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      dispatch(setLoading(true));
      setPagination(newPagination);
    },
    [dispatch],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      dispatch(setLoading(true));
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (
      key: keyof VouchersReportFilters,
      value: string | string[] | undefined,
    ) => {
      dispatch(setLoading(true));
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  return {
    items,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    exportParams: buildFilterSortParams(filters, sorting),
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
